import { lazy } from 'react';
import i18next from 'i18next';
import { ACCOUNT_ROUTES } from './account';
import permissions from '@shared/config/permissions';
import { PatientsIcon, DashboardIcon } from '@shared/ui/icons';
import {
  selectActivePatientFullName,
  selectActivePatientUuid,
} from 'redux_store/selectors/activePatientSelectors';

// Patient name from the active-patient slice is only authoritative when
// its uuid matches the uuid in the current URL. While navigating between
// patients (e.g. from Lucy to Vasya) the slice still holds the previous
// patient for one render. Returning an empty string here makes the
// breadcrumb widget hide this entry entirely until the slice catches up,
// which avoids both the stale-name flash and a "Patient" placeholder
// flicker — the trail just shows "Patients" alone for that brief window.
const resolvePatientLabel = ({ params, state }) => {
  const activeUuid = selectActivePatientUuid(state);
  if (activeUuid && activeUuid === params.patientUuid) {
    return selectActivePatientFullName(state) || '';
  }
  return '';
};

const PatientsList = lazy(() => import('@pages/patients'));
const PatientDashboard = lazy(() => import('@pages/patient-dashboard'));
const PatientEdit = lazy(() => import('@pages/patient-edit'));

const PATIENT_ROUTE_LIST_DS = {
  path: '/:currentAccountUuid/accounts/:accountUuid/patients',
  exact: true,
  parentRoute: ACCOUNT_ROUTES.listDS,
  additional: {
    component: PatientsList,
    protected: true,
    title: i18next.t('titles:patients'),
    icon: PatientsIcon,
    breadcrumb: {
      isRoot: false,
      // Chain straight to the Accounts list (the root the user
      // actually visits when navigating into a DS child account).
      // The earlier `account.editDS` link surfaced an extra
      // "Account name" crumb on deep-link reload that never appears
      // in normal navigation — a recognisable mismatch between
      // stack-based history and B2 parent-chain rebuild.
      parentPath: '/:currentAccountUuid/accounts',
      label: ({ state }) => state.i18n?.en?.titles?.patients || 'Patients',
    },
  },
};

const PATIENT_ROUTE_DASHBOARD_DS = {
  path: '/:currentAccountUuid/accounts/:accountUuid/patients/:patientUuid/:tableName/',
  exact: true,
  parentRoute: PATIENT_ROUTE_LIST_DS,
  additional: {
    component: PatientDashboard,
    protected: true,
    title: i18next.t('titles:patientDashboard'),
    icon: DashboardIcon,
    breadcrumb: {
      isRoot: false,
      parentPath: '/:currentAccountUuid/accounts/:accountUuid/patients',
      // Tab switches inside Patient Dashboard collapse on the same entry
      // — same rationale as the current-account variant.
      replaceSibling: true,
      label: resolvePatientLabel,
    },
  },
};

const PATIENT_ROUTE_EDIT_DS = {
  path: '/:currentAccountUuid/accounts/:accountUuid/patients/:patientUuid/edit',
  exact: true,
  parentRoute: PATIENT_ROUTE_DASHBOARD_DS,
  additional: {
    component: PatientEdit,
    protected: true,
    permission: permissions.PATIENT_PATCH,
    title: i18next.t('titles:editPatient'),
    breadcrumb: {
      isRoot: false,
      parentPath: '/:currentAccountUuid/accounts/:accountUuid/patients/:patientUuid/:tableName/',
      // Patient edit URL has no `:tableName`; seed the patient
      // dashboard's default tab so the breadcrumb back-link resolves
      // without `Missing ":tableName" param` on deep-link reload.
      parentParams: { tableName: 'assessments' },
      label: resolvePatientLabel,
    },
  },
};

const PATIENT_ROUTE_LIST = {
  path: '/:currentAccountUuid/patients',
  exact: true,
  additional: {
    component: PatientsList,
    protected: true,
    title: i18next.t('titles:patients'),
    icon: PatientsIcon,
    breadcrumb: {
      isRoot: true,
      label: ({ state }) => state.i18n?.en?.titles?.patients || 'Patients',
    },
  },
};

const PATIENT_ROUTE_DASHBOARD = {
  path: '/:currentAccountUuid/patients/:patientUuid/:tableName/',
  exact: true,
  parentRoute: PATIENT_ROUTE_LIST,
  additional: {
    component: PatientDashboard,
    protected: true,
    title: i18next.t('titles:patientDashboard'),
    icon: DashboardIcon,
    breadcrumb: {
      isRoot: false,
      parentPath: '/:currentAccountUuid/patients',
      // Tab switches inside Patient Dashboard (assessments / orders / claims)
      // change the :tableName segment but represent the same logical page —
      // collapse them into a single entry instead of stacking duplicates.
      replaceSibling: true,
      label: resolvePatientLabel,
    },
  },
};

const PATIENT_ROUTE_EDIT = {
  path: '/:currentAccountUuid/patients/:patientUuid/edit',
  exact: true,
  parentRoute: PATIENT_ROUTE_DASHBOARD,
  additional: {
    component: PatientEdit,
    protected: true,
    permission: permissions.PATIENT_PATCH,
    title: i18next.t('titles:editPatient'),
    breadcrumb: {
      isRoot: false,
      parentPath: '/:currentAccountUuid/patients/:patientUuid/:tableName/',
      // See PATIENT_ROUTE_EDIT_DS for context — patient edit has no
      // `:tableName`, seed the dashboard's default tab.
      parentParams: { tableName: 'assessments' },
      label: resolvePatientLabel,
    },
  },
};

export const PATIENT_ROUTES = {
  // ------- For child accounts ------
  listDS: PATIENT_ROUTE_LIST_DS,
  dashboardDS: PATIENT_ROUTE_DASHBOARD_DS,
  editDS: PATIENT_ROUTE_EDIT_DS,

  // ------- For current accounts ------
  list: PATIENT_ROUTE_LIST,
  dashboard: PATIENT_ROUTE_DASHBOARD,
  edit: PATIENT_ROUTE_EDIT,
};
